import { Link, useHistory } from 'react-router-dom';
import Image from '../atoms/Image';
import Time from '../atoms/Time';

import { useEffect, useState } from 'react';
import noImage from '../../assets/images/noImage-PC.png';
import STArticleCard from '../../assets/styles/molecules/articleCard.module.scss';
import STVideoModal from '../../assets/styles/organisms/videoModal.module.scss';
import { CheckSpFlag } from '../../hooks/CheckSpFlag';
import { ACCESS_LOG_ACTION } from '../../types/TAppAccessLogAction';
import { format } from '../../types/api/TApiDatetime';
import { postMemberAccessLog } from '../../utils/api/postMemberAccessLog';
import setEmbedScript from '../../utils/setEmbedScript';
import playButtonImage from '../atoms/play_video_button.png';
import VideoModal from '../organisms/VideoModal';
import LikeCard from './LikeCard';
import BookmarkCard from './BookmarkCard';
import { CONTENT_TYPE } from '../../types/TAppContentType';

type Props = {
  title: string;
  className: string;
  linkUrl: string;
  thumbnailUrl: string | null;
  thumbnailAlt: string;
  createdAt: string;
  goodCount: number;
  contentTypeId?: number;
  content?: string;
  url?: string;
  isRecommended?: boolean;
  likeFlg: boolean;
  bookmarkFlg: boolean;
};

const ArticleCard: React.FC<Props> = (props: Props) => {
  const history = useHistory();
  const spFlag = CheckSpFlag();
  const isVideo = props.contentTypeId === CONTENT_TYPE.video;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const hasContent = Boolean(props.content);
  // PCで動画は本文(embed HTML)をモーダル再生に使うため本文が必要
  const shouldOpenVideoModal = isVideo && hasContent && !spFlag;

  const handleModalOpen = () => {
    if (!shouldOpenVideoModal) return;
    setIsModalOpen(true);
    const videoUrl = window.location.origin + props.linkUrl;
    postMemberAccessLog(
      ACCESS_LOG_ACTION.contentDetail,
      document.referrer,
      videoUrl,
    );
  };
  const handleModalClose = () => setIsModalOpen(false);

  // 動画カード全体クリック: PCはモーダル、SP等は詳細へ
  const handleVideoCardClick = () => {
    if (shouldOpenVideoModal) {
      handleModalOpen();
      return;
    }
    history.push(props.linkUrl);
  };

  const renderVideo = (cardProps: Props) => (
    <div className={STArticleCard.videoWrapper}>
      <img
        src={playButtonImage}
        alt="再生ボタン"
        className={STArticleCard.play_button}
      />
      <div
        style={{
          backgroundImage: `url(${cardProps.thumbnailUrl || noImage})`,
          backgroundSize: `${cardProps.thumbnailUrl || 'auto'}`,
        }}
        className={`${STArticleCard.article_card_video_thumbnail_img} ${
          cardProps.thumbnailUrl
            ? STArticleCard.article_card_object_fit
            : STArticleCard.article_card_object_fit_none
        }`}
      ></div>
    </div>
  );

  const TikTokEmbed: React.FC<{ content: string }> = ({ content }) => {
    useEffect(() => {
      setEmbedScript('TikTok', content);
    }, [content]);

    return (
      <EmbedContainer>
        <div dangerouslySetInnerHTML={{ __html: content }} />
      </EmbedContainer>
    );
  };

  const InstagramEmbed: React.FC<{ content: string }> = ({ content }) => {
    useEffect(() => {
      setEmbedScript('Instagram', content);
    }, [content]);
    return (
      <EmbedContainer>
        <div
          className={STVideoModal.instagram}
          dangerouslySetInnerHTML={{ __html: content }}
        />
      </EmbedContainer>
    );
  };

  const EmbedContainer: React.FC = ({ children }) => (
    <div
      style={{
        width: '100%',
        height: '100%',
      }}
    >
      {children}
    </div>
  );

  const getEmbedComponent = (content: string | undefined) => {
    if (!content) return null;

    if (content.includes('tiktok.com')) {
      return <TikTokEmbed content={content} />;
    } else if (content.includes('instagram.com')) {
      return <InstagramEmbed content={content} />;
    } else {
      return (
        <EmbedContainer>
          <div dangerouslySetInnerHTML={{ __html: content }} />
        </EmbedContainer>
      );
    }
  };

  const EmbedComponent = getEmbedComponent(props.content);

  // 内側に Link を置かず、外側ラッパー1つだけで遷移/モーダルする
  // VideoModal はクリック領域の外（article の兄弟）に置く。
  // Portal 経由でも React 合成イベントはツリーを辿ってバブリングするため、
  // article 内に置くとモーダル内クリックが handleVideoCardClick を再発火する。
  const cardBody = (
    <>
      <div className={STArticleCard.article_card_thumbnail}>
        {isVideo ? (
          renderVideo(props)
        ) : props.isRecommended ? (
          <div className={STArticleCard.img}>
            <Image
              alt={props.thumbnailAlt}
              imageUrl={props.thumbnailUrl || noImage}
              className={STArticleCard.article_card_thumbnail_img_new}
              shownDefault={!props.thumbnailUrl}
            />
            <div className={STArticleCard.likeCardContainer}>
              <LikeCard count={props.goodCount} likeFlg={props.likeFlg} />
            </div>
          </div>
        ) : (
          // 通常記事はサムネを直接置き、余計なラップで高さ制約が崩れるのを防ぐ
          <Image
            alt={props.thumbnailAlt}
            imageUrl={props.thumbnailUrl || noImage}
            className={`${STArticleCard.article_card_thumbnail_img} ${
              props.thumbnailUrl
                ? STArticleCard.article_card_object_fit
                : STArticleCard.article_card_object_fit_none
            }`}
            shownDefault={!props.thumbnailUrl}
          />
        )}
      </div>

      {!props.isRecommended && (
        <div className={STArticleCard.article_card_article_txt}>
          <div className={STArticleCard.article_card_article_title}>
            <div className={STArticleCard.article_title}>
              <p>{props.title}</p>
            </div>
            <div className={STArticleCard.article_card_created_goodcount}>
              <Time
                className={STArticleCard.article_card_article_time}
                time={format(props.createdAt, 'yyyy.MM.dd')}
              />
              {!isVideo && (
                <div className={STArticleCard.article_actionbox}>
                  <BookmarkCard bookmarkFlg={props.bookmarkFlg} />
                  <LikeCard count={props.goodCount} likeFlg={props.likeFlg} />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );

  const videoModal = isModalOpen ? (
    <VideoModal
      EmbedComponent={EmbedComponent}
      isModalOpen={isModalOpen}
      onClose={handleModalClose}
    />
  ) : null;

  if (isVideo) {
    return (
      <>
        {videoModal}
        <article
          className={`${props.className} ${STArticleCard.clickableCard}`}
          onClick={handleVideoCardClick}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleVideoCardClick();
            }
          }}
          role="button"
          aria-label={`${props.title} を再生`}
          tabIndex={0}
        >
          {cardBody}
        </article>
      </>
    );
  }

  return (
    <article className={props.className}>
      <Link to={props.linkUrl} className={STArticleCard.clickableCard}>
        {cardBody}
      </Link>
    </article>
  );
};
export default ArticleCard;
